// Configuraciones para gráficos usando Chart.js

export interface ChartColors {
  primary: string;
  secondary: string;
  success: string;
  danger: string;
  warning: string;
  info: string;
  light: string;
  dark: string;
}

export class ChartConfig {
  
  // Paleta de colores principal
  static readonly COLORS: ChartColors = {
    primary: '#3B82F6',   // blue-500
    secondary: '#6B7280', // gray-500  
    success: '#10B981',   // green-500
    danger: '#EF4444',    // red-500
    warning: '#F59E0B',   // yellow-500
    info: '#06B6D4',      // cyan-500
    light: '#F3F4F6',     // gray-100
    dark: '#1F2937'       // gray-800
  };

  // Paleta extendida para gráficos con muchos datos
  static readonly EXTENDED_COLORS = [
    '#3B82F6', // blue-500
    '#10B981', // green-500
    '#F59E0B', // yellow-500
    '#EF4444', // red-500
    '#8B5CF6', // violet-500
    '#06B6D4', // cyan-500
    '#F97316', // orange-500
    '#EC4899', // pink-500
    '#84CC16', // lime-500
    '#6366F1', // indigo-500
    '#14B8A6', // teal-500
    '#F43F5E', // rose-500
  ];

  // Configuración base para todos los gráficos
  static getBaseConfig() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
              family: "'Inter', sans-serif"
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: '#374151',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true,
          titleFont: {
            size: 13,
            weight: 'bold'
          },
          bodyFont: {
            size: 12
          }
        }
      }
    };
  }

  // Configuración para gráfico de KPIs (dona pequeña)
  static getKPIDonutConfig() {
    return {
      ...this.getBaseConfig(),
      cutout: '75%',
      plugins: {
        ...this.getBaseConfig().plugins,
        legend: {
          display: false
        }
      }
    };
  }

  // Configuración para gráfico de distribución por estado
  static getStatusDistributionConfig() {
    return {
      ...this.getBaseConfig(),
      cutout: '50%',
      plugins: {
        ...this.getBaseConfig().plugins,
        legend: {
          ...this.getBaseConfig().plugins.legend,
          position: 'right' as const
        }
      }
    };
  }

  // Configuración para gráfico de tendencia temporal.
  // Usa eje X de tipo `category` con strings de fecha pre-formateadas para
  // evitar la dependencia de `chartjs-adapter-date-fns` (no instalada).
  // Si en el futuro se quiere zoom temporal o granularidades finas, migrar
  // a `type: 'time'` requiere agregar el adapter al bundle.
  static getTrendLineConfig() {
    return {
      ...this.getBaseConfig(),
      scales: {
        x: {
          type: 'category' as const,
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 11
            },
            // Cuando hay muchos puntos, Chart.js skipea labels automáticamente
            // si autoSkip está en true (default). Con padding suficiente la
            // legibilidad se mantiene.
            autoSkip: true,
            maxRotation: 45,
            minRotation: 0
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: '#F3F4F6'
          },
          ticks: {
            font: {
              size: 11
            }
          }
        }
      },
      elements: {
        line: {
          tension: 0.4,
          borderWidth: 3
        },
        point: {
          radius: 3,
          hoverRadius: 6
        }
      }
    };
  }

  // Configuración para gráficos de barras horizontales
  static getHorizontalBarConfig() {
    return {
      ...this.getBaseConfig(),
      indexAxis: 'y' as const,
      scales: {
        x: {
          beginAtZero: true,
          grid: {
            color: '#F3F4F6'
          },
          ticks: {
            font: {
              size: 11
            }
          }
        },
        y: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 11
            }
          }
        }
      }
    };
  }

  // Configuración para gráficos de barras verticales
  static getVerticalBarConfig() {
    return {
      ...this.getBaseConfig(),
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 11
            }
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: '#F3F4F6'
          },
          ticks: {
            font: {
              size: 11
            }
          }
        }
      }
    };
  }

  // Configuración para gráfico de área
  static getAreaConfig() {
    return {
      ...this.getBaseConfig(),
      fill: true,
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 11
            }
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: '#F3F4F6'
          },
          ticks: {
            font: {
              size: 11
            }
          }
        }
      },
      elements: {
        line: {
          tension: 0.4,
          borderWidth: 2
        },
        point: {
          radius: 0,
          hoverRadius: 4
        }
      }
    };
  }

  // Obtener colores para estados de pólizas
  static getStatusColors() {
    return {
      'ACTIVE': this.COLORS.success,
      'PENDING': this.COLORS.warning,
      'ERROR': this.COLORS.danger,
      'COMPLETED': this.COLORS.info
    };
  }

  // Generar dataset para gráfico de dona
  static createDonutDataset(data: any[], labelKey: string, valueKey: string, colors?: string[]) {
    const usedColors = colors || this.EXTENDED_COLORS;
    
    return {
      labels: data.map(item => item[labelKey]),
      datasets: [{
        data: data.map(item => item[valueKey]),
        backgroundColor: data.map((_, index) => usedColors[index % usedColors.length]),
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };
  }

  // Generar dataset para gráfico de barras
  static createBarDataset(data: any[], labelKey: string, valueKey: string, color?: string) {
    return {
      labels: data.map(item => item[labelKey]),
      datasets: [{
        label: 'Cantidad',
        data: data.map(item => item[valueKey]),
        backgroundColor: color || this.COLORS.primary,
        borderColor: color || this.COLORS.primary,
        borderWidth: 1
      }]
    };
  }

  // Generar dataset para gráfico de línea temporal
  static createTimelineDataset(data: any[], dateKey: string, valueKey: string, label: string, color?: string) {
    return {
      labels: data.map(item => item[dateKey]),
      datasets: [{
        label: label,
        data: data.map(item => item[valueKey]),
        borderColor: color || this.COLORS.primary,
        backgroundColor: color ? color + '20' : this.COLORS.primary + '20',
        fill: true,
        tension: 0.4
      }]
    };
  }

  // Formatear números para tooltips
  static formatTooltipNumber(value: number): string {
    return new Intl.NumberFormat('es-ES').format(value);
  }

  // Formatear porcentajes
  static formatPercentage(value: number): string {
    return `${value.toFixed(1)}%`;
  }
}
